import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import {
  MXP_FC_REVIEW_VISIT,
  MXP_FC_SETTLE,
  MXP_FC_WITHDRAW_COLLATERAL,
} from '../../mixpanel-events';
import { CARD_CRYPTODRAFT_WITHDRAWABLE } from '../../queries';
import { cryptodraftScreens } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';
import { useSetting } from '../../../../utilities/sharedHooks/settings';

const CryptodarftDetails = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const { state, openScreen, updateIsExtendCryptodraft } = useContext(CryptodraftContext);
  const [ledgerSupportEmailAddress] = useSetting('server_configurations', 'support_email_address');

  const principalCurrency = state?.principalCurrency;
  const fiatCurrency = state?.fiatCurrency;
  const cardCryptodraftDetails = useMemo(
    () => state?.cryptodraftDetails ?? {},
    [state?.cryptodraftDetails]
  );
  const isUSUser = state?.user?.isUS;

  const {
    cryptodraft_ltv_ranges: _cryptodraftLtvRanges,
    cryptodraft_interest_mpr: _cryptodraftInterestMpr,
    cryptodraft_us_ltv_ranges: _cryptodraftUSLtvRanges,
    cryptodraft_us_interest_mpr: _cryptodraftUSInterestMpr,
  } = state?.cryptodraftSettings ?? {
    cryptodraft_ltv_ranges: '',
    cryptodraft_interest_mpr: '',
    cryptodraft_us_ltv_ranges: '',
    cryptodraft_us_interest_mpr: '',
  };

  const { data: withdrawable } = useQuery(CARD_CRYPTODRAFT_WITHDRAWABLE, { cacheTime: 0 });

  const cryptodraftPrincipleToDispalyCurrencyRate = useRate(
    cardCryptodraftDetails?.principal_currency,
    principalCurrency
  );

  const cryptodraftCollateralCurrencyToDisplayCurrencyRate = useRate(
    cardCryptodraftDetails?.collateral_currency,
    fiatCurrency
  );

  const cryptodraftSpendingLimit =
    cardCryptodraftDetails?.spending_limit *
    currencies[cardCryptodraftDetails?.principal_currency?.toUpperCase()]?.conversion *
    cryptodraftPrincipleToDispalyCurrencyRate;

  const cryptodraftPrincipleNetAmount =
    cardCryptodraftDetails?.principal_net_amount *
    currencies[cardCryptodraftDetails?.principal_currency?.toUpperCase()].conversion *
    cryptodraftPrincipleToDispalyCurrencyRate;

  const cryptodraftInterestMpr = useMemo(
    () => (isUSUser ? _cryptodraftUSInterestMpr : _cryptodraftInterestMpr),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_cryptodraftUSInterestMpr, _cryptodraftInterestMpr, isUSUser]
  );

  const cryptodraftLtvRanges = useMemo(
    () => (isUSUser ? _cryptodraftUSLtvRanges : _cryptodraftLtvRanges),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_cryptodraftLtvRanges, _cryptodraftUSLtvRanges, isUSUser]
  );

  const ltvRanges = useMemo(() => {
    const interestValues = cryptodraftInterestMpr?.split(',');

    if (
      cardCryptodraftDetails &&
      cardCryptodraftDetails?.ranges_with_available_spending &&
      interestValues
    ) {
      const ranges = cardCryptodraftDetails?.ranges_with_available_spending?.map((item, index) => ({
        index,
        start: item.start,
        end: item.end,
        interest: interestValues[index],
        availableSpending: item.available_spending,
      }));

      return ranges;
    }
    return [];
  }, [cardCryptodraftDetails, cryptodraftInterestMpr]);

  const canWithdrawCollateral = useMemo(() => {
    const ltvRangesValues = cryptodraftLtvRanges?.split(',').map((item) => item.split('-'));

    if (ltvRangesValues) {
      const lowestRange = ltvRangesValues[0];
      const highestInLowestRange = lowestRange[1];

      return cardCryptodraftDetails?.ltv_percentage < highestInLowestRange;
    }
    return false;
  }, [cardCryptodraftDetails, cryptodraftLtvRanges]);

  const withdrawalDisabled = useMemo(
    () =>
      (withdrawable?.withdrawable_amount || 0) <= 0 ||
      parseInt(cardCryptodraftDetails?.principal_pending_spent_amount, 10) > 0,
    [cardCryptodraftDetails?.principal_pending_spent_amount, withdrawable?.withdrawable_amount]
  );

  const disabledSettleAmount = useMemo(() => {
    if (parseInt(cardCryptodraftDetails?.principal_pending_spent_amount, 10) > 0) return true;
    if (
      cardCryptodraftDetails?.principal_net_amount -
        cardCryptodraftDetails?.principal_pending_spent_amount >
      0
    ) {
      return false;
    }
    return true;
  }, [cardCryptodraftDetails]);

  useEffect(() => {
    sendStats(MXP_FC_REVIEW_VISIT, {}, FLOWS.FC);
  }, []);

  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() => history.push('/dashboard/card/details')}
      title={translate('cryptodraft_details_title')}>
      <div className="d-flex flex-row justify-content-between align-items-center mt-4">
        <p className="text-f6 text-shadow">{translate('cryptodraft_details_limit_spent')}</p>
        <p className="text-f6 text-dark">
          {`${formatNumberWithComma(
            formatNumber(cryptodraftPrincipleNetAmount, exchange.FIAT_VALUE)
          )} / `}
          <span className="text-shadow">{`${formatNumberWithComma(
            formatNumber(cryptodraftSpendingLimit, exchange.FIAT_VALUE)
          )} ${principalCurrency?.toUpperCase()}`}</span>
        </p>
      </div>
      <div className="progress mt-2 bg-disabled">
        <div
          className="progress-bar bg-white"
          style={{
            width: `${
              (cardCryptodraftDetails?.principal_net_amount /
                cardCryptodraftDetails?.spending_limit) *
              100
            }%`,
          }}
        />
      </div>
      <div className="d-flex flex-row my-4 rounded-extra bg-lighter p-3">
        <p className="text-dark text-f6 d-flex align-items-center">
          {translate('cryptodraft_review_total_title')}
        </p>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {formatNumber(
              cardCryptodraftDetails?.collateral_amount *
                currencies[cardCryptodraftDetails?.collateral_currency?.toUpperCase()].conversion,
              exchange.CRYPTO_VALUE
            )}{' '}
            {cardCryptodraftDetails?.collateral_currency?.toUpperCase()}
          </b>
          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(
              formatNumber(
                cardCryptodraftDetails?.collateral_amount *
                  currencies[cardCryptodraftDetails?.collateral_currency?.toUpperCase()]
                    .conversion *
                  cryptodraftCollateralCurrencyToDisplayCurrencyRate,
                exchange.FIAT_VALUE
              )
            )}{' '}
            {fiatCurrency}
          </p>
        </span>
      </div>
      <div className="rounded-extra bg-lighter mt-3 mb-4 card-calculator__summary">
        <div className="d-flex flex-row justify-content-between p-3">
          <p className="text-dark text-f6 d-flex align-items-center">
            {`${translate('cryptodraft_details_current_ltv')}%`}
          </p>
          <p className="text-f6">{`${cardCryptodraftDetails?.ltv_percentage} %`}</p>
        </div>
        <div className="d-flex flex-row justify-content-between p-3">
          <p className="text-dark text-f6 d-flex align-items-center">
            {translate('cryptodraft_details_paying_mpr')}
          </p>
          <p className="text-f6">{`${cardCryptodraftDetails?.mpr_interest} %`}</p>
        </div>
      </div>
      <div className="mt-2">
        <p className="text-f6 text-shadow p-0">
          {translate('cryptodraft_calculator_remaining_spend_note')}
        </p>
      </div>
      <div className="rounded-extra bg-lighter my-3 card-calculator__summary">
        <div className="d-flex flex-column p-0">
          {ltvRanges.map((item) => (
            <React.Fragment key={item.index}>
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <p className="card-calculator__summary--item-note text-dark text-f6">
                    {translate('cryptodraft_calculator_mpr_note')
                      .replace('%d1', `${item.start}`)
                      .replace('%d2', `-${item.end}`)
                      .replace('%d3', `${item.interest}`)}
                  </p>
                  <p className="text-f6">
                    {` ${formatNumberWithComma(
                      formatNumber(
                        item.availableSpending *
                          currencies[cardCryptodraftDetails?.principal_currency.toUpperCase()]
                            .conversion *
                          cryptodraftPrincipleToDispalyCurrencyRate,
                        exchange.FIAT_VALUE
                      )
                    )} ${principalCurrency?.toUpperCase()}`}
                  </p>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      {canWithdrawCollateral && (
        <div className="mt-3">
          <button
            type="button"
            className="btn text-primary text-btn-item"
            disabled={withdrawalDisabled}
            onClick={() => {
              sendStats(MXP_FC_WITHDRAW_COLLATERAL, {}, FLOWS.FC);
              openScreen(cryptodraftScreens.WITHDRAW, { openedFromDetails: true });
            }}>
            <b>{translate('cryptodraft_collateral_withdrawal_btn')}</b>
          </button>
        </div>
      )}
      {parseInt(cardCryptodraftDetails?.principal_pending_spent_amount, 10) > 0 && (
        <div className="alert alert-warning d-flex mt-4 cursor-pointer" role="presentation">
          <i className="icon icon-info-icon position-relative start-0" />
          <div className="content">
            {translate('cryptodraft_pending_balance_warning_note').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            )}
          </div>
        </div>
      )}
      <div className="btn-container">
        <button
          type="button"
          disabled={disabledSettleAmount}
          className="btn border border-disabled py-3 w-100 text-btn-item"
          onClick={() => {
            sendStats(MXP_FC_SETTLE, {}, FLOWS.FC);
            openScreen(cryptodraftScreens.SETTLE);
          }}>
          {translate('settle_btn_title')}
        </button>
        <button
          type="button"
          className="btn btn-primary py-3 w-100"
          onClick={() => {
            updateIsExtendCryptodraft();
            openScreen(cryptodraftScreens.CALCULATOR);
          }}>
          {translate('extend_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default CryptodarftDetails;
